/* ------------------------------------------------------------------ *
 * Canvas badge renderer.                                              *
 *                                                                     *
 * The badge is drawn directly with the Canvas 2D API instead of being *
 * rasterized from the DOM with html-to-image. Canvas works reliably   *
 * on every device (including mobile Safari/Chrome), and because the   *
 * on-page preview and the downloaded file use this exact same code,   *
 * what you see is exactly what you get.                               *
 * ------------------------------------------------------------------ */

export const BADGE_W = 1440
export const BADGE_H = 1080

export const BADGE_ASSETS = {
  cts: "/badge/CTS_3D.png",
  wiks: "/badge/wiks.png",
  chennailabs: "/badge/chennailabs.png",
  tekiknow: "/badge/tekiknow.png",
  bg: "/badge/badge_texture5.jpg",
}

const ARCHIVO = "Archivo, sans-serif"
const GROTESK = "'Space Grotesk', sans-serif"
const FRAUNCES = "'Fraunces', serif" // distinctive italic serif for the presentation title

/* ---- asset loading (cached) ---- */
const imgCache = {}
function loadImage(src) {
  if (imgCache[src]) return imgCache[src]
  const p = new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
  imgCache[src] = p
  return p
}

export async function ensureFonts() {
  try {
    await Promise.all([
      document.fonts.load('900 80px "Archivo"'),
      document.fonts.load('800 26px "Archivo"'),
      document.fonts.load('700 30px "Archivo"'),
      document.fonts.load('500 27px "Space Grotesk"'),
      document.fonts.load('600 18px "Space Grotesk"'),
      document.fonts.load('italic 600 30px "Fraunces"'),
    ])
  } catch {}
  try {
    await document.fonts.ready
  } catch {}
}

export async function prepareBadge() {
  const [bg, cts, wiks, chennailabs, tekiknow] = await Promise.all([
    loadImage(BADGE_ASSETS.bg).catch(() => null),
    loadImage(BADGE_ASSETS.cts).catch(() => null),
    loadImage(BADGE_ASSETS.wiks).catch(() => null),
    loadImage(BADGE_ASSETS.chennailabs).catch(() => null),
    loadImage(BADGE_ASSETS.tekiknow).catch(() => null),
  ])
  await ensureFonts()
  return { bg, cts, wiks, chennailabs, tekiknow }
}

export function loadPhoto(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null)
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

/* ---- small drawing helpers ---- */
function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

// Letter-spaced text. Returns total width. align: left|center|right
function drawText(ctx, text, x, y, { font, color, ls = 0, align = "left", baseline = "top", shadow }) {
  ctx.font = font
  ctx.fillStyle = color
  ctx.textBaseline = baseline
  if (shadow) {
    ctx.shadowColor = shadow.color
    ctx.shadowBlur = shadow.blur
    ctx.shadowOffsetY = shadow.dy || 0
  }
  let total
  if (!ls) {
    total = ctx.measureText(text).width
    let sx = x
    if (align === "center") sx = x - total / 2
    else if (align === "right") sx = x - total
    ctx.textAlign = "left"
    ctx.fillText(text, sx, y)
  } else {
    ctx.textAlign = "left"
    total = -ls
    for (const ch of text) total += ctx.measureText(ch).width + ls
    let cx = x
    if (align === "center") cx = x - total / 2
    else if (align === "right") cx = x - total
    for (const ch of text) {
      ctx.fillText(ch, cx, y)
      cx += ctx.measureText(ch).width + ls
    }
  }
  ctx.shadowColor = "transparent"
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  return total
}

function measure(ctx, text, font, ls = 0) {
  ctx.font = font
  if (!ls) return ctx.measureText(text).width
  let total = -ls
  for (const ch of text) total += ctx.measureText(ch).width + ls
  return total
}

function wrapText(ctx, text, font, maxW) {
  ctx.font = font
  const words = text.split(/\s+/).filter(Boolean)
  const lines = []
  let cur = ""
  for (const w of words) {
    const test = cur ? cur + " " + w : w
    if (!cur || ctx.measureText(test).width <= maxW) cur = test
    else {
      lines.push(cur)
      cur = w
    }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : [""]
}

function coverDrawCircle(ctx, img, cx, cy, radius, boxSize, offX, offY) {
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.clip()
  const boxCx = cx + offX
  const boxCy = cy + offY
  const s = Math.max(boxSize / img.width, boxSize / img.height)
  const dw = img.width * s
  const dh = img.height * s
  ctx.drawImage(img, boxCx - dw / 2, boxCy - dh / 2, dw, dh)
  ctx.restore()
}

/* small inline icons (24px viewBox), stroked gold */
function drawCalendar(ctx, x, y, size) {
  const s = size / 24
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(s, s)
  ctx.strokeStyle = "#ffc94f"
  ctx.lineWidth = 2
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  roundRect(ctx, 3, 4, 18, 18, 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(16, 2); ctx.lineTo(16, 6)
  ctx.moveTo(8, 2); ctx.lineTo(8, 6)
  ctx.moveTo(3, 10); ctx.lineTo(21, 10)
  ctx.stroke()
  ctx.restore()
}

function drawPin(ctx, x, y, size) {
  const s = size / 24
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(s, s)
  ctx.strokeStyle = "#ffc94f"
  ctx.lineWidth = 2
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.stroke(new Path2D("M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"))
  ctx.beginPath()
  ctx.arc(12, 10, 3, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
}

/* ------------------------------------------------------------------ */
/* Draw the whole badge in 1440x1080 logical coordinates.              */
/* (Caller is responsible for any device-pixel scaling.)               */
/* ------------------------------------------------------------------ */
export function drawBadge(ctx, props, imgs) {
  const { name, jobTitle, company, talk, photoImg, zoom = 1, posX = 0, posY = 0, variant = "attending" } = props
  const { bg, cts, wiks, chennailabs, tekiknow } = imgs
  const isModerating = variant === "moderating"
  // "moderating" shares the presenting layout (date line + hashtags); only the pill differs.
  const isPresenting = variant === "presenting" || isModerating

  ctx.clearRect(0, 0, BADGE_W, BADGE_H)

  // Background
  ctx.fillStyle = "#0a1330"
  ctx.fillRect(0, 0, BADGE_W, BADGE_H)
  if (bg) {
    const s = Math.max(BADGE_W / bg.width, BADGE_H / bg.height)
    const dw = bg.width * s
    const dh = bg.height * s
    ctx.drawImage(bg, (BADGE_W - dw) / 2, (BADGE_H - dh) / 2, dw, dh)
  }

  // Navy tint
  ctx.fillStyle = "rgba(7,14,38,0.16)"
  ctx.fillRect(0, 0, BADGE_W, BADGE_H)

  // Header + bottom gradient
  const g = ctx.createLinearGradient(0, 0, 0, BADGE_H)
  g.addColorStop(0, "rgba(6,12,34,0.34)")
  g.addColorStop(0.2, "rgba(6,12,34,0)")
  g.addColorStop(0.62, "rgba(6,12,34,0)")
  g.addColorStop(0.86, "rgba(5,10,28,0.74)")
  g.addColorStop(1, "rgba(4,8,24,0.96)")
  ctx.fillStyle = g
  ctx.fillRect(0, 0, BADGE_W, BADGE_H)

  // Vignette
  const vc = ctx.createRadialGradient(720, 454, 520, 720, 454, 920)
  vc.addColorStop(0, "rgba(0,0,0,0)")
  vc.addColorStop(1, "rgba(0,0,0,0.42)")
  ctx.fillStyle = vc
  ctx.fillRect(0, 0, BADGE_W, BADGE_H)

  const PAD = 64

  /* ---------- Top bar ---------- */
  // CTS logo
  let logoBottom = 170
  if (cts) {
    const lw = 184
    const lh = (cts.height / cts.width) * lw
    ctx.drawImage(cts, PAD, 46, lw, lh)
    logoBottom = 46 + lh
    const tx = PAD + lw + 18
    const blockCy = 46 + lh / 2
    drawText(ctx, "CANDELA TECHNOLOGY SUMMIT", tx, blockCy - 22, {
      font: `800 24px ${ARCHIVO}`, color: "#ffffff", ls: 3, baseline: "alphabetic",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 14, dy: 2 },
    })
    drawText(ctx, "Chennai Edition · 2026", tx, blockCy + 18, {
      font: `600 18px ${GROTESK}`, color: "#ffd45e", ls: 1, baseline: "alphabetic",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 12, dy: 2 },
    })
  }

  // Date / venue chips (top-right)
  const chips = [
    { icon: drawCalendar, text: "July 3, 2026" },
    { icon: drawPin, text: "Vivanta, Chennai" },
  ]
  const chipH = 42
  let cy = 46
  for (const chip of chips) {
    const tw = measure(ctx, chip.text, `600 21px ${GROTESK}`)
    const chipW = 20 + 20 + 9 + tw + 20
    const cx = BADGE_W - PAD - chipW
    ctx.fillStyle = "rgba(255,255,255,0.12)"
    ctx.strokeStyle = "rgba(255,201,79,0.35)"
    ctx.lineWidth = 1
    roundRect(ctx, cx, cy, chipW, chipH, chipH / 2)
    ctx.fill()
    ctx.stroke()
    chip.icon(ctx, cx + 20, cy + (chipH - 20) / 2, 20)
    drawText(ctx, chip.text, cx + 20 + 20 + 9, cy + chipH / 2, {
      font: `600 21px ${GROTESK}`, color: "#ffffff", baseline: "middle",
      shadow: { color: "rgba(0,0,0,0.6)", blur: 10 },
    })
    cy += chipH + 10
  }

  /* ---------- Middle ---------- */
  const panelTop = 968
  const midTop = logoBottom + 24
  const midBottom = panelTop - 20
  const midCy = (midTop + midBottom) / 2

  // Left column: photo + pill
  const photoD = 372
  const photoR = photoD / 2
  const navyR = photoR + 5
  const goldR = navyR + 11
  const pillH = 52
  const leftColH = goldR * 2 + 24 + pillH
  const leftColTop = midCy - leftColH / 2
  const photoCx = PAD + goldR
  const photoCy = leftColTop + goldR

  // gold ring with glow
  ctx.save()
  ctx.shadowColor = "rgba(245,164,0,0.45)"
  ctx.shadowBlur = 70
  const ring = ctx.createLinearGradient(photoCx - goldR, photoCy - goldR, photoCx + goldR, photoCy + goldR)
  ring.addColorStop(0, "#ffd45e")
  ring.addColorStop(0.55, "#f5a400")
  ring.addColorStop(1, "#e08e00")
  ctx.fillStyle = ring
  ctx.beginPath()
  ctx.arc(photoCx, photoCy, goldR, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  // navy inset
  ctx.fillStyle = "#0a1330"
  ctx.beginPath()
  ctx.arc(photoCx, photoCy, navyR, 0, Math.PI * 2)
  ctx.fill()

  // photo or placeholder
  if (photoImg) {
    coverDrawCircle(ctx, photoImg, photoCx, photoCy, photoR, photoD * zoom, posX * photoD, posY * photoD)
  } else {
    const pg = ctx.createRadialGradient(photoCx, photoCy - photoR * 0.12, 20, photoCx, photoCy, photoR)
    pg.addColorStop(0, "#24407a")
    pg.addColorStop(1, "#0c1838")
    ctx.fillStyle = pg
    ctx.beginPath()
    ctx.arc(photoCx, photoCy, photoR, 0, Math.PI * 2)
    ctx.fill()
    // Clean person glyph (lucide-style: head circle + shoulders arc).
    const s = photoR * 0.04
    ctx.save()
    ctx.translate(photoCx - 12 * s, photoCy - 15 * s)
    ctx.scale(s, s)
    ctx.fillStyle = "rgba(255,255,255,0.5)"
    ctx.beginPath()
    ctx.arc(12, 8, 4.2, 0, Math.PI * 2)
    ctx.fill()
    ctx.fill(new Path2D("M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"))
    ctx.restore()
    drawText(ctx, "Your photo", photoCx, photoCy + photoR * 0.52, {
      font: `600 24px ${GROTESK}`, color: "rgba(255,255,255,0.6)", align: "center", baseline: "middle",
    })
  }

  // pill
  const pillLabel = isModerating ? "I'M MODERATING" : isPresenting ? "I'M PRESENTING" : "I'M ATTENDING"
  const labelW = measure(ctx, pillLabel, `800 26px ${ARCHIVO}`, 5)
  const pillW = 34 + 12 + 14 + labelW + 34
  const pillX = photoCx - pillW / 2
  const pillY = leftColTop + goldR * 2 + 24
  const pillGrad = ctx.createLinearGradient(0, pillY, 0, pillY + pillH)
  pillGrad.addColorStop(0, "#ffc01f")
  pillGrad.addColorStop(1, "#f5a400")
  ctx.fillStyle = pillGrad
  roundRect(ctx, pillX, pillY, pillW, pillH, pillH / 2)
  ctx.fill()
  ctx.fillStyle = "#10204f"
  ctx.beginPath()
  ctx.arc(pillX + 34 + 6, pillY + pillH / 2, 6, 0, Math.PI * 2)
  ctx.fill()
  drawText(ctx, pillLabel, pillX + 34 + 12 + 14, pillY + pillH / 2 + 1, {
    font: `800 26px ${ARCHIVO}`, color: "#10204f", ls: 5, baseline: "middle",
  })

  /* ---------- Right column ---------- */
  const rx = PAD + goldR * 2 + 60
  const rw = BADGE_W - rx - PAD
  const display = (name || "Your Name").trim().toUpperCase()
  const nameLenSrc = (name || "Your Name").trim()
  const nameSize = nameLenSrc.length > 24 ? 58 : nameLenSrc.length > 18 ? 70 : nameLenSrc.length > 12 ? 82 : 92
  const nameFont = `900 ${nameSize}px ${ARCHIVO}`
  const nameLines = wrapText(ctx, display, nameFont, rw)
  const nameLH = nameSize * 1.04
  const hasTitle = (jobTitle || "").trim().length > 0
  const hasCompany = (company || "").trim().length > 0

  // Presentation / talk title — ONLY on the "I'm presenting" badge. It is drawn
  // lower down, just after the "Catch my session" line, in a distinctive italic
  // serif (Fraunces). Wrapped (titles are long), capped at 3 lines.
  const talkFont = props.talkFont || `italic 600 34px ${FRAUNCES}`
  const talkLH = props.talkLH || 40
  // Honor explicit line breaks ("\n") in the title so wrap points can be hand-set;
  // each segment is still word-wrapped as a safety net. Capped at 3 lines.
  const talkLines = isPresenting && (talk || "").trim()
    ? talk.trim().split("\n").flatMap((seg) => wrapText(ctx, seg.trim(), talkFont, rw)).slice(0, 3)
    : []
  const showTalk = talkLines.length > 0

  // total height for vertical centering
  let total = nameLines.length * nameLH
  if (hasTitle) total += 14 + 38
  if (hasCompany) total += 6 + 35
  total += 28 + 28 // connect
  if (isPresenting) {
    total += 16 + 32 // date · place line
    total += 12 + 26 // captions (hashtags)
    if (showTalk) total += 22 + 30 + 8 + talkLines.length * talkLH // "catch my session" + title
  } else {
    total += 16 + 35 // tagline
    total += 12 + 26 // hashtag
  }

  let yy = midCy - total / 2

  // name
  for (const line of nameLines) {
    drawText(ctx, line, rx, yy, {
      font: nameFont, color: "#ffffff", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.75)", blur: 28, dy: 4 },
    })
    yy += nameLH
  }

  if (hasTitle) {
    yy += 14
    drawText(ctx, jobTitle, rx, yy, {
      font: `700 32px ${ARCHIVO}`, color: "#ffd45e", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    yy += 38
  }
  if (hasCompany) {
    yy += 6
    drawText(ctx, company, rx, yy, {
      font: `500 27px ${GROTESK}`, color: "rgba(255,255,255,0.9)", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    yy += 35
  }

  // LET'S CONNECT
  yy += 28
  const connY = yy + 12
  ctx.fillStyle = "#ffd45e"
  ctx.beginPath()
  ctx.arc(rx + 4.5, connY, 4.5, 0, Math.PI * 2)
  ctx.fill()
  const connLabelX = rx + 16
  const connW = drawText(ctx, "LET'S CONNECT", connLabelX, connY, {
    font: `700 25px ${ARCHIVO}`, color: "#ffffff", ls: 6, baseline: "middle",
    shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
  })
  const lineX = connLabelX + connW + 16
  const lineW = Math.min(220, rx + rw - lineX)
  if (lineW > 20) {
    const lg = ctx.createLinearGradient(lineX, 0, lineX + lineW, 0)
    lg.addColorStop(0, "#ffd45e")
    lg.addColorStop(1, "rgba(255,212,94,0)")
    ctx.fillStyle = lg
    ctx.fillRect(lineX, connY - 1, lineW, 2)
  }
  yy += 28

  if (isPresenting) {
    // Date · place line
    yy += 16
    ctx.textBaseline = "top"
    const d1 = "July 3, 2026   ·   "
    const d1w = drawText(ctx, d1, rx, yy, {
      font: `500 25px ${GROTESK}`, color: "rgba(255,255,255,0.94)", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    drawText(ctx, "Chennai 2026", rx + d1w, yy, {
      font: `700 25px ${GROTESK}`, color: "#ffd45e", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    yy += 32

    // Captions (hashtags)
    yy += 12
    drawText(ctx, "#CTS2026  ·  #CandelaTechnologySummit", rx, yy, {
      font: `700 20px ${ARCHIVO}`, color: "rgba(255,212,94,0.92)", ls: 3, baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 14, dy: 2 },
    })
    yy += 26

    // "Catch my session" kicker + the talk title, anchored at the bottom (dark band)
    if (showTalk) {
      yy += 22
      ctx.fillStyle = "#ffd45e"
      ctx.beginPath()
      ctx.arc(rx + 4.5, yy + 11, 4.5, 0, Math.PI * 2)
      ctx.fill()
      drawText(ctx, "CATCH MY SESSION", rx + 16, yy + 11, {
        font: `700 20px ${ARCHIVO}`, color: "#ffffff", ls: 4, baseline: "middle",
        shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
      })
      yy += 30

      yy += 8
      for (const line of talkLines) {
        // dark halo pass keeps the gold title legible over bright background areas
        drawText(ctx, line, rx, yy, {
          font: talkFont, color: "rgba(4,8,22,0.92)", baseline: "top",
          shadow: { color: "rgba(4,8,22,0.95)", blur: 16, dy: 0 },
        })
        drawText(ctx, line, rx, yy, {
          font: talkFont, color: "#ffd45e", baseline: "top",
          shadow: { color: "rgba(0,0,0,0.55)", blur: 6, dy: 2 },
        })
        yy += talkLH
      }
    }
  } else {
    // tagline (attending)
    yy += 16
    ctx.textBaseline = "top"
    const t1 = "See you at the summit — "
    const t1w = drawText(ctx, t1, rx, yy, {
      font: `500 27px ${GROTESK}`, color: "rgba(255,255,255,0.94)", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    drawText(ctx, "Chennai 2026", rx + t1w, yy, {
      font: `700 27px ${GROTESK}`, color: "#ffd45e", baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 16, dy: 2 },
    })
    yy += 35

    // hashtag
    yy += 12
    drawText(ctx, "#CTS2026  ·  #CandelaTechnologySummit", rx, yy, {
      font: `700 20px ${ARCHIVO}`, color: "rgba(255,212,94,0.92)", ls: 3, baseline: "top",
      shadow: { color: "rgba(0,0,0,0.7)", blur: 14, dy: 2 },
    })
  }

  /* ---------- Bottom partner / sponsor panel ---------- */
  ctx.fillStyle = "rgba(255,255,255,0.97)"
  ctx.fillRect(0, panelTop, BADGE_W, BADGE_H - panelTop)

  const labelFont = `700 13px ${ARCHIVO}`
  const labelLS = 2.5
  const wiksH = 38
  const wiksW = wiks ? (wiks.width / wiks.height) * wiksH : 0
  const clH = 22
  const clW = chennailabs ? (chennailabs.width / chennailabs.height) * clH : 0
  const tkH = 30 // taller than chennailabs to offset tekiknow's baked-in tagline line
  const tkW = tekiknow ? (tekiknow.width / tekiknow.height) * tkH : 0
  const sponsorGap = clW && tkW ? 28 : 0
  const sponsorsW = clW + sponsorGap + tkW
  const laW = measure(ctx, "TECHNICAL PARTNER", labelFont, labelLS)
  const lbW = measure(ctx, "SUMMIT PARTNERS", labelFont, labelLS)
  const gAW = Math.max(laW, wiksW)
  const gBW = Math.max(lbW, sponsorsW)
  const gap = 44
  const totalW = gAW + gap + 1 + gap + gBW
  const gx = (BADGE_W - totalW) / 2
  const gACx = gx + gAW / 2
  const dx = gx + gAW + gap
  const gBCx = dx + 1 + gap + gBW / 2
  const labelY = panelTop + 22
  const logoCy = panelTop + 72

  drawText(ctx, "TECHNICAL PARTNER", gACx, labelY, { font: labelFont, color: "#4ea220", ls: labelLS, align: "center", baseline: "top" })
  if (wiks) ctx.drawImage(wiks, gACx - wiksW / 2, logoCy - wiksH / 2, wiksW, wiksH)

  ctx.strokeStyle = "#dde7d4"
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(dx + 0.5, panelTop + 22)
  ctx.lineTo(dx + 0.5, BADGE_H - 22)
  ctx.stroke()

  drawText(ctx, "SUMMIT PARTNERS", gBCx, labelY, { font: labelFont, color: "#4ea220", ls: labelLS, align: "center", baseline: "top" })
  let sx = gBCx - sponsorsW / 2
  if (chennailabs) {
    ctx.drawImage(chennailabs, sx, logoCy - clH / 2, clW, clH)
    sx += clW + sponsorGap
  }
  if (tekiknow) ctx.drawImage(tekiknow, sx, logoCy - tkH / 2, tkW, tkH)
}
